import React, { useState, useEffect } from 'react';
import { 
  heroAPI, 
  featureCardAPI, 
  importantDateAPI, 
  statisticAPI, 
  teacherAPI, 
  companyAPI,
  webCourseAPI,
  courseRegistrationFormAPI,
  formQuestionAPI,
  formResultAPI,
  contactUsAPI,
  webCourseFaqAPI,
  cacheUtils 
} from '../../services/api';
import { DataTable } from '../ui/data-table';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../ui/modal';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import DefaultEditor from 'react-simple-wysiwyg';
import { formatRegistrationSubmittedAt } from '../../utils/registrationDate';

const WebConfig = () => {
  const [activeTab, setActiveTab] = useState('hero');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Data states for each section
  const [heroes, setHeroes] = useState([]);
  const [featureCards, setFeatureCards] = useState([]);
  const [importantDates, setImportantDates] = useState([]);
  const [statistics, setStatistics] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [webCourses, setWebCourses] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [deletingId, setDeletingId] = useState(null);

  // Web Courses nested modals
  const [isFormsModalOpen, setIsFormsModalOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState(false);
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
  const [isSubmissionDetailsModalOpen, setIsSubmissionDetailsModalOpen] = useState(false);
  const [isFaqsModalOpen, setIsFaqsModalOpen] = useState(false);
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [courseForms, setCourseForms] = useState([]);
  const [formQuestions, setFormQuestions] = useState([]);
  const [formResults, setFormResults] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [courseFaqs, setCourseFaqs] = useState([]);
  const [formFormData, setFormFormData] = useState({});
  const [questionFormData, setQuestionFormData] = useState({});
  const [faqFormData, setFaqFormData] = useState({});

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      switch (activeTab) {
        case 'hero':
          const heroData = await heroAPI.getAllHeroes();
          setHeroes(heroData?.data || []);
          break;
        case 'featureCards':
          const cardsData = await featureCardAPI.getAllFeatureCards();
          setFeatureCards(cardsData?.data || []);
          break;
        case 'importantDates':
          const datesData = await importantDateAPI.getAllImportantDates();
          setImportantDates(datesData?.data || []);
          break;
        case 'statistics':
          const statsData = await statisticAPI.getAllStatistics();
          setStatistics(statsData?.data || []);
          break;
        case 'teachers':
          const teachersData = await teacherAPI.getAllTeachers();
          setTeachers(teachersData?.data || []);
          break;
        case 'companies':
          const companiesData = await companyAPI.getAllCompanies();
          setCompanies(companiesData?.data || []);
          break;
        case 'webCourses':
          const coursesData = await webCourseAPI.getAllWebCourses();
          setWebCourses(coursesData?.data || []);
          break;
        case 'contactUs':
          const contactData = await contactUsAPI.getAllContactMessages();
          setContactMessages(contactData?.data || []);
          break;
        default:
          break;
      }
    } catch (err) {
      setError(`Failed to fetch ${activeTab} data`);
      console.error(`Error fetching ${activeTab}:`, err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreate = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormData(getDefaultFormData());
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setIsEditing(true);
    setEditingId(item.id);
    setFormData({ ...item });
    setIsModalOpen(true);
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      
      switch (activeTab) {
        case 'featureCards':
          await featureCardAPI.toggleFeatureCard(id, { is_active: newStatus });
          break;
        case 'importantDates':
          await importantDateAPI.toggleImportantDate(id, { is_active: newStatus });
          break;
        case 'statistics':
          await statisticAPI.toggleStatistic(id, { is_active: newStatus });
          break;
        case 'teachers':
          await teacherAPI.toggleTeacher(id, { is_active: newStatus });
          break;
        case 'companies':
          await companyAPI.toggleCompany(id, { is_active: newStatus });
          break;
        case 'webCourses':
          await webCourseAPI.toggleWebCourse(id, { is_active: newStatus });
          break;
        default:
          break;
      }
      
      cacheUtils.clearWebDataCache();
      await fetchData();
    } catch (err) {
      console.error('Error toggling active status:', err);
      alert('Failed to toggle active status');
    }
  };

  const handleActivateHero = async (id) => {
    try {
      await heroAPI.activateHero(id);
      cacheUtils.clearWebDataCache();
      await fetchData();
    } catch (err) {
      console.error('Error activating hero:', err);
      alert('Failed to activate hero');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      setDeletingId(id);
      
      switch (activeTab) {
        case 'hero':
          await heroAPI.deleteHero(id);
          break;
        case 'featureCards':
          await featureCardAPI.deleteFeatureCard(id);
          break;
        case 'importantDates':
          await importantDateAPI.deleteImportantDate(id);
          break;
        case 'statistics':
          await statisticAPI.deleteStatistic(id);
          break;
        case 'teachers':
          await teacherAPI.deleteTeacher(id);
          break;
        case 'companies':
          await companyAPI.deleteCompany(id);
          break;
        case 'webCourses':
          await webCourseAPI.deleteWebCourse(id);
          break;
        case 'contactUs':
          await contactUsAPI.deleteContactMessage(id);
          break;
        default:
          break;
      }
      
      cacheUtils.clearWebDataCache();
      await fetchData();
    } catch (err) {
      console.error('Error deleting item:', err);
      alert('Failed to delete item');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      if (isEditing) {
        switch (activeTab) {
          case 'hero':
            await heroAPI.updateHero(editingId, formData);
            break;
          case 'featureCards':
            await featureCardAPI.updateFeatureCard(editingId, formData);
            break;
          case 'importantDates':
            await importantDateAPI.updateImportantDate(editingId, formData);
            break;
          case 'statistics':
            await statisticAPI.updateStatistic(editingId, formData);
            break;
          case 'teachers':
            await teacherAPI.updateTeacher(editingId, formData);
            break;
          case 'companies':
            await companyAPI.updateCompany(editingId, formData);
            break;
          case 'webCourses':
            await webCourseAPI.updateWebCourse(editingId, formData);
            break;
          case 'contactUs':
            await contactUsAPI.updateContactMessage(editingId, formData);
            break;
          default:
            break;
        }
      } else {
        switch (activeTab) {
          case 'hero':
            await heroAPI.createHero(formData);
            break;
          case 'contactUs':
            await contactUsAPI.createContactMessage(formData);
            break;
          case 'featureCards':
            await featureCardAPI.createFeatureCard(formData);
            break;
          case 'importantDates':
            await importantDateAPI.createImportantDate(formData);
            break;
          case 'statistics':
            await statisticAPI.createStatistic(formData);
            break;
          case 'teachers':
            await teacherAPI.createTeacher(formData);
            break;
          case 'companies':
            await companyAPI.createCompany(formData);
            break;
          case 'webCourses':
            console.log('Creating web course with data:', formData);
            await webCourseAPI.createWebCourse(formData);
            break;
          default:
            break;
        }
      }
      
      cacheUtils.clearWebDataCache();
      setIsModalOpen(false);
      setFormData({});
      await fetchData();
    } catch (err) {
      console.error('Error saving data:', err);
      alert('Failed to save data');
    } finally {
      setLoading(false);
    }
  };

  const getDefaultFormData = () => {
    switch (activeTab) {
      case 'hero':
        return { title: '', subtitle: '', background_image: '' };
      case 'featureCards':
        return { icon: '', title: '', description: '', display_order: 1 };
      case 'importantDates':
        return { date: '', event: '', link: '#', display_order: 1 };
      case 'statistics':
        return { value: '', label: '', display_order: 1 };
      case 'teachers':
        return { name: '', title: '', image: '', bio: '', display_order: 1 };
      case 'companies':
        return { name: '', logo: '', website: '#', display_order: 1 };
      case 'webCourses':
        return { 
          name: '', 
          course_type: '', 
          icon: '', 
          description: '', 
          intro_video: '', 
          intro_text: '', 
          course_details: '', 
          course_materials: '', 
          duration: '', 
          price: 0, 
          language: '',
          is_active: true
        };
      case 'contactUs':
        return { 
          name: '', 
          email: '', 
          subject: '', 
          message: '' 
        };
      default:
        return {};
    }
  };

  // Web Courses nested functions
  const handleManageForms = async (course) => {
    setSelectedCourse(course);
    try {
      setLoading(true);
      // Clear cache before fetching to ensure fresh data
      cacheUtils.clearWebDataCache();
      const formsData = await courseRegistrationFormAPI.getActiveCourseRegistrationForms();
      // Filter forms for this course
      const filtered = formsData?.data?.filter(f => f.web_course_id === course.id) || [];
      setCourseForms(filtered);
      setIsFormsModalOpen(true);
    } catch (err) {
      console.error('Error fetching forms:', err);
      alert('Failed to fetch forms');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormFormData({ 
      web_course_id: selectedCourse.id,
      title: '', 
      description: '', 
      is_active: true 
    });
    setIsFormModalOpen(true);
  };

  const handleEditForm = (form) => {
    setIsEditing(true);
    setEditingId(form.id);
    setFormFormData({ ...form });
    setIsFormModalOpen(true);
  };

  const handleDeleteForm = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this form? All questions and submissions will also be deleted.')) {
      return;
    }

    try {
      await courseRegistrationFormAPI.deleteCourseRegistrationForm(formId);
      cacheUtils.clearWebDataCache();
      await handleManageForms(selectedCourse);
    } catch (err) {
      console.error('Error deleting form:', err);
      alert('Failed to delete form');
    }
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      if (isEditing) {
        await courseRegistrationFormAPI.updateCourseRegistrationForm(editingId, formFormData);
      } else {
        await courseRegistrationFormAPI.createCourseRegistrationForm(formFormData);
      }
      
      cacheUtils.clearWebDataCache();
      setIsFormModalOpen(false);
      setFormFormData({});
      await handleManageForms(selectedCourse);
    } catch (err) {
      console.error('Error saving form:', err);
      alert('Failed to save form');
    } finally {
      setLoading(false);
    }
  };

  const handleManageQuestions = async (form) => {
    setSelectedForm(form);
    try {
      setLoading(true);
      // Clear cache before fetching to ensure fresh data
      cacheUtils.clearWebDataCache();
      const questionsData = await formQuestionAPI.getFormQuestionsByFormId(form.id);
      setFormQuestions(questionsData?.data || []);
      setIsQuestionsModalOpen(true);
    } catch (err) {
      console.error('Error fetching questions:', err);
      alert('Failed to fetch questions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuestion = () => {
    setIsEditing(false);
    setEditingId(null);
    setQuestionFormData({
      form_id: selectedForm.id,
      question_text: '',
      question_type: 'text',
      is_required: true,
      options: '',
      display_order: 1
    });
    setIsQuestionModalOpen(true);
  };

  const handleEditQuestion = (question) => {
    setIsEditing(true);
    setEditingId(question.id);
    setQuestionFormData({ ...question });
    setIsQuestionModalOpen(true);
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      await formQuestionAPI.deleteFormQuestion(questionId);
      cacheUtils.clearWebDataCache();
      await handleManageQuestions(selectedForm);
    } catch (err) {
      console.error('Error deleting question:', err);
      alert('Failed to delete question');
    }
  };

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      if (isEditing) {
        await formQuestionAPI.updateFormQuestion(editingId, questionFormData);
      } else {
        await formQuestionAPI.createFormQuestion(questionFormData);
        const questionsData = await formQuestionAPI.getFormQuestionsByFormId(selectedForm.id);
        setFormQuestions(questionsData?.data || []);
      }
      
      cacheUtils.clearWebDataCache();
      setIsQuestionModalOpen(false);
      setQuestionFormData({});
    } catch (err) {
      console.error('Error saving question:', err);
      alert('Failed to save question');
    } finally {
      setLoading(false);
    }
  };

  const handleViewResults = async (form) => {
    setSelectedForm(form);
    try {
      setLoading(true);
      const [resultsData, questionsData] = await Promise.all([
        formResultAPI.getFormResultsByFormId(form.id),
        formQuestionAPI.getFormQuestionsByFormId(form.id)
      ]);
      setFormResults(resultsData?.data || []);
      setFormQuestions(questionsData?.data || []);
      setIsResultsModalOpen(true);
    } catch (err) {
      console.error('Error fetching results:', err);
      alert('Failed to fetch form results');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFormActive = async (formId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await courseRegistrationFormAPI.toggleCourseRegistrationForm(formId, { is_active: newStatus });
      cacheUtils.clearWebDataCache();
      await handleManageForms(selectedCourse);
    } catch (err) {
      console.error('Error toggling form status:', err);
      alert('Failed to toggle form status');
    }
  };

  const renderSubmissionAnswers = (result) => {
    const answers = Array.isArray(result.answers)
      ? result.answers
      : result.form_data && typeof result.form_data === 'object'
        ? Object.entries(result.form_data).map(([key, value]) => ({ key, value }))
        : [];
    const sortedQuestions = [...formQuestions].sort((a, b) => a.display_order - b.display_order);

    if (answers.length === 0) {
      return <span className="text-gray-500">No responses provided.</span>;
    }

    return (
      <dl className="space-y-2">
        {answers.map((answer, index) => {
          const isFallbackAnswer = answer && typeof answer === 'object' && 'key' in answer;
          const value = isFallbackAnswer ? answer.value : answer;
          const label = isFallbackAnswer
            ? answer.key
            : sortedQuestions[index]?.question_text || `Question ${index + 1}`;

          return (
            <div key={`${label}-${index}`}>
              <dt className="font-medium text-gray-700">{label}</dt>
              <dd className="mt-0.5 whitespace-pre-wrap text-gray-900">
                {Array.isArray(value) || (value && typeof value === 'object')
                  ? JSON.stringify(value)
                  : String(value ?? '')}
              </dd>
            </div>
          );
        })}
      </dl>
    );
  };

  const downloadFormResults = (format = 'csv') => {
    if (!formResults || formResults.length === 0) {
      alert('No data to download');
      return;
    }

    try {
      // Prepare data for export
      const exportData = formResults.map(result => {
        const flatData = {
          'ID': result.id,
          'Email': result.user_email,
          'Submitted At': formatRegistrationSubmittedAt(result.submitted_at)
        };

        // Map answers to question text using formQuestions
        if (formQuestions && formQuestions.length > 0) {
          console.log('Mapping answers with questions:', formQuestions, result.answers);
          // Sort questions by display_order to match answer indices
          const sortedQuestions = [...formQuestions].sort((a, b) => a.display_order - b.display_order);
          
          if (result.answers && Array.isArray(result.answers)) {
            result.answers.forEach((answer, index) => {
              if (sortedQuestions[index]) {
                flatData[sortedQuestions[index].question_text] = answer;
              }
            });
          }
        }

        // Also include form_data if it exists (for backward compatibility)
        if (result.form_data && typeof result.form_data === 'object') {
          Object.keys(result.form_data).forEach(key => {
            if (!flatData[key]) { // Don't override if already set from answers
              flatData[key] = result.form_data[key];
            }
          });
        }

        return flatData;
      });

      if (format === 'csv') {
        // Generate CSV
        const headers = Object.keys(exportData[0]);
        const csvContent = [
          headers.join(','),
          ...exportData.map(row => 
            headers.map(header => {
              const value = row[header];
              // Escape commas and quotes in values
              if (value && (value.toString().includes(',') || value.toString().includes('"'))) {
                return `"${value.toString().replace(/"/g, '""')}"`;
              }
              return value || '';
            }).join(',')
          )
        ].join('\n');

        // Download CSV with UTF-8 BOM for proper encoding
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `form_submissions_${selectedForm?.id}_${Date.now()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (format === 'xlsx') {
        // Generate Excel XLSX
        const headers = Object.keys(exportData[0]);
        
        // Create worksheet data
        const wsData = [
          headers,
          ...exportData.map(row => headers.map(header => row[header] || ''))
        ];

        // Convert to CSV format (Excel can open CSV as XLSX)
        const csvContent = wsData.map(row => 
          row.map(cell => {
            const value = cell?.toString() || '';
            // Escape commas and quotes
            if (value.includes(',') || value.includes('"') || value.includes('\n')) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(',')
        ).join('\n');

        // Add BOM for UTF-8 Excel compatibility
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `form_submissions_${selectedForm?.id}_${Date.now()}.xlsx`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Error downloading form results:', err);
      alert('Failed to download form results');
    }
  };

  // FAQ Management Functions
  const handleManageFaqs = async (course) => {
    setSelectedCourse(course);
    try {
      setLoading(true);
      cacheUtils.clearWebDataCache();
      const faqsData = await webCourseFaqAPI.getWebCourseFaqsByCourseId(course.id);
      setCourseFaqs(faqsData?.data || []);
      setIsFaqsModalOpen(true);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      alert('Failed to fetch FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFaq = () => {
    setIsEditing(false);
    setEditingId(null);
    setFaqFormData({ 
      web_course_id: selectedCourse.id,
      question: '', 
      answer: '', 
      display_order: (courseFaqs.length || 0) + 1
    });
    setIsFaqModalOpen(true);
  };

  const handleEditFaq = (faq) => {
    setIsEditing(true);
    setEditingId(faq.id);
    setFaqFormData({ ...faq });
    setIsFaqModalOpen(true);
  };

  const handleDeleteFaq = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) {
      return;
    }

    try {
      setLoading(true);
      await webCourseFaqAPI.deleteWebCourseFaq(faqId);
      cacheUtils.clearWebDataCache();
      
      // Fetch updated FAQs
      const faqsData = await webCourseFaqAPI.getWebCourseFaqsByCourseId(selectedCourse.id);
      setCourseFaqs(faqsData?.data || []);
    } catch (err) {
      console.error('Error deleting FAQ:', err);
      alert('Failed to delete FAQ');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFaq = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      if (isEditing) {
        await webCourseFaqAPI.updateWebCourseFaq(editingId, faqFormData);
      } else {
        await webCourseFaqAPI.createWebCourseFaq(faqFormData);
      }
      
      // Close modal and reset form first
      setIsFaqModalOpen(false);
      setFaqFormData({});
      setIsEditing(false);
      setEditingId(null);
      
      // Clear cache and fetch updated FAQs
      cacheUtils.clearWebDataCache();
      const faqsData = await webCourseFaqAPI.getWebCourseFaqsByCourseId(selectedCourse.id);
      setCourseFaqs(faqsData?.data || []);
    } catch (err) {
      console.error('Error saving FAQ:', err);
      alert('Failed to save FAQ');
    } finally {
      setLoading(false);
    }
  };

  const getColumns = () => {
    const baseActions = (row) => (
      <div className="flex space-x-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleEdit(row)}
          className="h-8 px-2"
        >
          Edit
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleDelete(row.id)}
          disabled={deletingId === row.id}
          className="h-8 px-2 bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
        >
          {deletingId === row.id ? 'Deleting...' : 'Delete'}
        </Button>
      </div>
    );

    switch (activeTab) {
      case 'hero':
        return [
          { accessorKey: 'id', header: 'ID', sortable: true },
          { accessorKey: 'title', header: 'Title', sortable: true },
          { accessorKey: 'subtitle', header: 'Subtitle', sortable: false },
          { 
            accessorKey: 'background_image', 
            header: 'Image', 
            cell: (row) => (
              <img src={row.background_image} alt="" className="h-10 w-20 object-cover rounded" />
            )
          },
          { 
            accessorKey: 'is_active', 
            header: 'Active', 
            cell: (row) => (
              <span className={`px-2 py-1 text-xs rounded ${row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                {row.is_active ? 'Yes' : 'No'}
              </span>
            )
          },
          {
            accessorKey: 'actions',
            header: 'Actions',
            cell: (row) => (
              <div className="flex space-x-2">
                {!row.is_active && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleActivateHero(row.id)}
                    className="h-8 px-2 bg-green-50 hover:bg-green-100 text-green-700"
                  >
                    Activate
                  </Button>
                )}
                {baseActions(row)}
              </div>
            )
          }
        ];

      case 'featureCards':
        return [
          { accessorKey: 'id', header: 'ID', sortable: true },
          { accessorKey: 'icon', header: 'Icon', sortable: false },
          { accessorKey: 'title', header: 'Title', sortable: true },
          { accessorKey: 'description', header: 'Description', sortable: false },
          { accessorKey: 'display_order', header: 'Order', sortable: true },
          { 
            accessorKey: 'is_active', 
            header: 'Active', 
            cell: (row) => (
              <button
                onClick={() => handleToggleActive(row.id, row.is_active)}
                className={`px-2 py-1 text-xs rounded ${row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
              >
                {row.is_active ? 'Active' : 'Inactive'}
              </button>
            )
          },
          { accessorKey: 'actions', header: 'Actions', cell: baseActions }
        ];

      case 'importantDates':
        return [
          { accessorKey: 'id', header: 'ID', sortable: true },
          { accessorKey: 'date', header: 'Date', sortable: true },
          { accessorKey: 'event', header: 'Event', sortable: true },
          { accessorKey: 'link', header: 'Link', sortable: false },
          { accessorKey: 'display_order', header: 'Order', sortable: true },
          { 
            accessorKey: 'is_active', 
            header: 'Active', 
            cell: (row) => (
              <button
                onClick={() => handleToggleActive(row.id, row.is_active)}
                className={`px-2 py-1 text-xs rounded ${row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
              >
                {row.is_active ? 'Active' : 'Inactive'}
              </button>
            )
          },
          { accessorKey: 'actions', header: 'Actions', cell: baseActions }
        ];

      case 'statistics':
        return [
          { accessorKey: 'id', header: 'ID', sortable: true },
          { accessorKey: 'value', header: 'Value', sortable: true },
          { accessorKey: 'label', header: 'Label', sortable: true },
          { accessorKey: 'display_order', header: 'Order', sortable: true },
          { 
            accessorKey: 'is_active', 
            header: 'Active', 
            cell: (row) => (
              <button
                onClick={() => handleToggleActive(row.id, row.is_active)}
                className={`px-2 py-1 text-xs rounded ${row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
              >
                {row.is_active ? 'Active' : 'Inactive'}
              </button>
            )
          },
          { accessorKey: 'actions', header: 'Actions', cell: baseActions }
        ];

      case 'teachers':
        return [
          { accessorKey: 'id', header: 'ID', sortable: true },
          { 
            accessorKey: 'image', 
            header: 'Image', 
            cell: (row) => (
              <img src={row.image} alt={row.name} className="h-10 w-10 object-cover rounded-full" />
            )
          },
          { accessorKey: 'name', header: 'Name', sortable: true },
          { accessorKey: 'title', header: 'Title', sortable: true },
          { accessorKey: 'display_order', header: 'Order', sortable: true },
          { 
            accessorKey: 'is_active', 
            header: 'Active', 
            cell: (row) => (
              <button
                onClick={() => handleToggleActive(row.id, row.is_active)}
                className={`px-2 py-1 text-xs rounded ${row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
              >
                {row.is_active ? 'Active' : 'Inactive'}
              </button>
            )
          },
          { accessorKey: 'actions', header: 'Actions', cell: baseActions }
        ];

      case 'companies':
        return [
          { accessorKey: 'id', header: 'ID', sortable: true },
          { 
            accessorKey: 'logo', 
            header: 'Logo', 
            cell: (row) => (
              <img src={row.logo} alt={row.name} className="h-10 w-20 object-contain" />
            )
          },
          { accessorKey: 'name', header: 'Name', sortable: true },
          { accessorKey: 'website', header: 'Website', sortable: false },
          { accessorKey: 'display_order', header: 'Order', sortable: true },
          { 
            accessorKey: 'is_active', 
            header: 'Active', 
            cell: (row) => (
              <button
                onClick={() => handleToggleActive(row.id, row.is_active)}
                className={`px-2 py-1 text-xs rounded ${row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
              >
                {row.is_active ? 'Active' : 'Inactive'}
              </button>
            )
          },
          { accessorKey: 'actions', header: 'Actions', cell: baseActions }
        ];

      case 'webCourses':
        return [
          { accessorKey: 'id', header: 'ID', sortable: true },
          { 
            accessorKey: 'icon', 
            header: 'Icon', 
            cell: (row) => (
              <img src={row.icon} alt={row.name} className="h-10 w-10 object-contain" />
            )
          },
          { accessorKey: 'name', header: 'Name', sortable: true },
          { accessorKey: 'course_type', header: 'Type', sortable: true },
          { accessorKey: 'duration', header: 'Duration', sortable: false },
          { accessorKey: 'price', header: 'Price', sortable: true, cell: (row) => `₮${row.price?.toLocaleString()}` },
          { accessorKey: 'language', header: 'Language', sortable: true },
          { 
            accessorKey: 'is_active', 
            header: 'Active', 
            cell: (row) => (
              <button
                onClick={() => handleToggleActive(row.id, row.is_active)}
                className={`px-2 py-1 text-xs rounded ${row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
              >
                {row.is_active ? 'Active' : 'Inactive'}
              </button>
            )
          },
          { 
            accessorKey: 'forms', 
            header: 'Forms', 
            cell: (row) => (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleManageForms(row)}
                className="h-8 px-2 bg-purple-50 hover:bg-purple-100 text-purple-700"
              >
                Manage Forms
              </Button>
            )
          },
          { 
            accessorKey: 'faqs', 
            header: 'FAQs', 
            cell: (row) => (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleManageFaqs(row)}
                className="h-8 px-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700"
              >
                Manage FAQs
              </Button>
            )
          },
          { accessorKey: 'actions', header: 'Actions', cell: baseActions }
        ];

      case 'contactUs':
        return [
          { accessorKey: 'id', header: 'ID', sortable: true },
          { accessorKey: 'name', header: 'Name', sortable: true },
          { accessorKey: 'email', header: 'Email', sortable: true },
          { accessorKey: 'subject', header: 'Subject', sortable: true },
          { 
            accessorKey: 'message', 
            header: 'Message', 
            cell: (row) => (
              <div className="max-w-md">
                <details className="cursor-pointer">
                  <summary className="text-blue-600 hover:text-blue-700">View Message</summary>
                  <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                    <p className="whitespace-pre-wrap">{row.message}</p>
                  </div>
                </details>
              </div>
            )
          },
          { 
            accessorKey: 'created_at', 
            header: 'Submitted At', 
            sortable: true,
            cell: (row) => new Date(row.created_at).toLocaleString()
          },
          { accessorKey: 'actions', header: 'Actions', cell: baseActions }
        ];

      default:
        return [];
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'hero': return heroes;
      case 'featureCards': return featureCards;
      case 'importantDates': return importantDates;
      case 'statistics': return statistics;
      case 'teachers': return teachers;
      case 'companies': return companies;
      case 'webCourses': return webCourses;
      case 'contactUs': return contactMessages;
      default: return [];
    }
  };

  const renderFormFields = () => {
    switch (activeTab) {
      case 'hero':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <Input
                value={formData.title || ''}
                onChange={(e) => handleInputChange('title', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle *</label>
              <textarea
                value={formData.subtitle || ''}
                onChange={(e) => handleInputChange('subtitle', e.target.value)}
                required
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Background Image URL *</label>
              <Input
                value={formData.background_image || ''}
                onChange={(e) => handleInputChange('background_image', e.target.value)}
                required
              />
            </div>
          </>
        );

      case 'featureCards':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Icon Path *</label>
              <Input
                value={formData.icon || ''}
                onChange={(e) => handleInputChange('icon', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <Input
                value={formData.title || ''}
                onChange={(e) => handleInputChange('title', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                required
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order *</label>
              <Input
                type="number"
                value={formData.display_order || 1}
                onChange={(e) => handleInputChange('display_order', parseInt(e.target.value))}
                required
              />
            </div>
          </>
        );

      case 'importantDates':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <Input
                value={formData.date || ''}
                onChange={(e) => handleInputChange('date', e.target.value)}
                required
                placeholder="e.g., Dec 15"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event *</label>
              <Input
                value={formData.event || ''}
                onChange={(e) => handleInputChange('event', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Link</label>
              <Input
                value={formData.link || '#'}
                onChange={(e) => handleInputChange('link', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order *</label>
              <Input
                type="number"
                value={formData.display_order || 1}
                onChange={(e) => handleInputChange('display_order', parseInt(e.target.value))}
                required
              />
            </div>
          </>
        );

      case 'statistics':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Value *</label>
              <Input
                value={formData.value || ''}
                onChange={(e) => handleInputChange('value', e.target.value)}
                required
                placeholder="e.g., 2018 or 500+"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Label *</label>
              <Input
                value={formData.label || ''}
                onChange={(e) => handleInputChange('label', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order *</label>
              <Input
                type="number"
                value={formData.display_order || 1}
                onChange={(e) => handleInputChange('display_order', parseInt(e.target.value))}
                required
              />
            </div>
          </>
        );

      case 'teachers':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <Input
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <Input
                value={formData.title || ''}
                onChange={(e) => handleInputChange('title', e.target.value)}
                required
                placeholder="e.g., CFA, MA"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Image URL *</label>
              <Input
                value={formData.image || ''}
                onChange={(e) => handleInputChange('image', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
              <DefaultEditor
                value={formData.bio || ''}
                onChange={(e) => handleInputChange('bio', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order *</label>
              <Input
                type="number"
                value={formData.display_order || 1}
                onChange={(e) => handleInputChange('display_order', parseInt(e.target.value))}
                required
              />
            </div>
          </>
        );

      case 'companies':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
              <Input
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL *</label>
              <Input
                value={formData.logo || ''}
                onChange={(e) => handleInputChange('logo', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <Input
                value={formData.website || '#'}
                onChange={(e) => handleInputChange('website', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order *</label>
              <Input
                type="number"
                value={formData.display_order || 1}
                onChange={(e) => handleInputChange('display_order', parseInt(e.target.value))}
                required
              />
            </div>
          </>
        );

      case 'webCourses':
        return (
          <>
            <div>
              <label htmlFor="web-course-active" className="block text-sm font-medium text-gray-700 mb-1">Active</label>
              <select
                id="web-course-active"
                value={formData.is_active === false ? 'inactive' : 'active'}
                onChange={(e) => handleInputChange('is_active', e.target.value === 'active')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">Inactive courses are hidden from the website.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Course Name *</label>
              <Input
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Course Type *</label>
              <Input
                value={formData.course_type || ''}
                onChange={(e) => handleInputChange('course_type', e.target.value)}
                required
                placeholder="e.g., CFA, FRM, ACCA"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Icon Path *</label>
              <Input
                value={formData.icon || ''}
                onChange={(e) => handleInputChange('icon', e.target.value)}
                required
                placeholder="e.g., /assets/cfa-icon.svg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <DefaultEditor
                value={formData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                required
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Brief description of the course"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Intro Video URL</label>
              <Input
                value={formData.intro_video || ''}
                onChange={(e) => handleInputChange('intro_video', e.target.value)}
                placeholder="https://example.com/intro-video.mp4"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Intro Text</label>
              <DefaultEditor
                value={formData.intro_text || ''}
                onChange={(e) => handleInputChange('intro_text', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Welcome message or introduction text"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Course Details</label>
              <DefaultEditor
                value={formData.course_details || ''}
                onChange={(e) => handleInputChange('course_details', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Topics include: Ethics, Quantitative Methods, etc."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Course Materials</label>
              <DefaultEditor
                value={formData.course_materials || ''}
                onChange={(e) => handleInputChange('course_materials', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Study notes, practice questions, mock exams, video lectures"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration *</label>
              <Input
                value={formData.duration || ''}
                onChange={(e) => handleInputChange('duration', e.target.value)}
                required
                placeholder="e.g., 6 months"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
              <Input
                type="number"
                step="0.01"
                value={formData.price || 0}
                onChange={(e) => handleInputChange('price', parseFloat(e.target.value))}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Language *</label>
              <Input
                value={formData.language || ''}
                onChange={(e) => handleInputChange('language', e.target.value)}
                required
                placeholder="e.g., English, Mongolian"
              />
            </div>
          </>
        );

      case 'contactUs':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <Input
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <Input
                type="email"
                value={formData.email || ''}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
              <Input
                value={formData.subject || ''}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
              <textarea
                value={formData.message || ''}
                onChange={(e) => handleInputChange('message', e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const tabs = [
    { id: 'hero', label: 'Hero Section' },
    { id: 'featureCards', label: 'Feature Cards' },
    { id: 'importantDates', label: 'Important Dates' },
    { id: 'statistics', label: 'Statistics' },
    { id: 'teachers', label: 'Teachers' },
    { id: 'companies', label: 'Companies' },
    { id: 'webCourses', label: 'Web Courses' },
    { id: 'contactUs', label: 'Contact Us' }
  ];

  return (
    <div className="max-w-7xl mx-auto p-8">
      <div className="mb-8 border-b border-gray-200 pb-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Website Configuration</h1>
        <p className="text-lg text-gray-600">Manage website content and settings</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Content */}
      <div className="bg-white shadow-md rounded-lg p-6">
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">
            {tabs.find(t => t.id === activeTab)?.label} ({getCurrentData().length})
          </h2>
          <Button
            onClick={handleCreate}
            className="bg-blue-600 hover:bg-blue-700"
          >
            + Create New
          </Button>
        </div>

        <DataTable
          columns={getColumns()}
          data={getCurrentData()}
          searchKey={activeTab === 'hero' ? 'title' : activeTab === 'featureCards' ? 'title' : activeTab === 'contactUs' ? 'email' : 'name'}
          searchPlaceholder="Search..."
          loading={loading}
          emptyMessage={`No ${tabs.find(t => t.id === activeTab)?.label.toLowerCase()} found.`}
        />
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setFormData({});
          setIsEditing(false);
          setEditingId(null);
        }}
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <form onSubmit={handleSubmit}>
          <ModalHeader>
            <h2 className="text-xl font-semibold">
              {isEditing ? 'Edit' : 'Create'} {tabs.find(t => t.id === activeTab)?.label}
            </h2>
          </ModalHeader>

          <ModalBody className="space-y-4 max-h-[60vh] overflow-y-auto">
            {renderFormFields()}
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsModalOpen(false);
                setFormData({});
                setIsEditing(false);
                setEditingId(null);
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Forms Management Modal */}
      <Modal
        isOpen={isFormsModalOpen}
        onClose={() => {
          setIsFormsModalOpen(false);
          setSelectedCourse(null);
          setCourseForms([]);
        }}
        className="max-w-6xl"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold">
            Manage Forms - {selectedCourse?.title}
          </h2>
        </ModalHeader>

        <ModalBody>
          <div className="mb-4">
            <Button
              onClick={handleCreateForm}
              className="bg-blue-600 hover:bg-blue-700"
            >
              + Create New Form
            </Button>
          </div>

          <DataTable
            columns={[
              { accessorKey: 'id', header: 'ID', sortable: true },
              { accessorKey: 'title', header: 'Title', sortable: true },
              { accessorKey: 'description', header: 'Description', sortable: false },
              { 
                accessorKey: 'is_active', 
                header: 'Active', 
                cell: (row) => (
                  <button
                    onClick={() => handleToggleFormActive(row.id, row.is_active)}
                    className={`px-2 py-1 text-xs rounded ${row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
                  >
                    {row.is_active ? 'Active' : 'Inactive'}
                  </button>
                )
              },
              {
                accessorKey: 'actions',
                header: 'Actions',
                cell: (row) => (
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleManageQuestions(row)}
                      className="h-8 px-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700"
                    >
                      Questions
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewResults(row)}
                      className="h-8 px-2 bg-green-50 hover:bg-green-100 text-green-700"
                    >
                      Results
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditForm(row)}
                      className="h-8 px-2"
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteForm(row.id)}
                      className="h-8 px-2 bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                    >
                      Delete
                    </Button>
                  </div>
                )
              }
            ]}
            data={courseForms}
            searchKey="title"
            searchPlaceholder="Search forms..."
            loading={loading}
            emptyMessage="No forms found for this course."
          />
        </ModalBody>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setIsFormsModalOpen(false);
              setSelectedCourse(null);
              setCourseForms([]);
            }}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Form Create/Edit Modal */}
      <Modal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setFormFormData({});
          setIsEditing(false);
          setEditingId(null);
        }}
        className="max-w-2xl"
      >
        <form onSubmit={handleSubmitForm}>
          <ModalHeader>
            <h2 className="text-xl font-semibold">
              {isEditing ? 'Edit' : 'Create'} Registration Form
            </h2>
          </ModalHeader>

          <ModalBody className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Form Title *</label>
              <Input
                value={formFormData.title || ''}
                onChange={(e) => setFormFormData(prev => ({ ...prev, title: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formFormData.description || ''}
                onChange={(e) => setFormFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsFormModalOpen(false);
                setFormFormData({});
                setIsEditing(false);
                setEditingId(null);
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Questions Management Modal */}
      <Modal  
        isOpen={isQuestionsModalOpen}
        onClose={() => {
          setIsQuestionsModalOpen(false);
          setSelectedForm(null);
          setFormQuestions([]);
        }}
        className="max-w-6xl max-h-[90vh]"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold">
            Manage Questions - {selectedForm?.title}
          </h2>
        </ModalHeader>

        <ModalBody className="max-h-[70vh] overflow-y-auto">
          <div className="mb-4">
            <Button
              onClick={handleCreateQuestion}
              className="bg-blue-600 hover:bg-blue-700"
            >
              + Add Question
            </Button>
          </div>

          <div className="overflow-y-auto">
            <DataTable
            columns={[
              { accessorKey: 'id', header: 'ID', sortable: true },
              { accessorKey: 'question_text', header: 'Question', sortable: true },
              { accessorKey: 'question_type', header: 'Type', sortable: true },
              { 
                accessorKey: 'is_required', 
                header: 'Required', 
                cell: (row) => (
                  <span className={`px-2 py-1 text-xs rounded ${row.is_required ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                    {row.is_required ? 'Yes' : 'No'}
                  </span>
                )
              },
              { accessorKey: 'display_order', header: 'Order', sortable: true },
              {
                accessorKey: 'actions',
                header: 'Actions',
                cell: (row) => (
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditQuestion(row)}
                      className="h-8 px-2"
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteQuestion(row.id)}
                      className="h-8 px-2 bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                    >
                      Delete
                    </Button>
                  </div>
                )
              }
            ]}
            data={formQuestions}
            searchKey="question_text"
            searchPlaceholder="Search questions..."
            loading={loading}
            emptyMessage="No questions found for this form."
          />
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setIsQuestionsModalOpen(false);
              setSelectedForm(null);
              setFormQuestions([]);
            }}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Question Create/Edit Modal */}
      <Modal
        isOpen={isQuestionModalOpen}
        onClose={() => {
          setIsQuestionModalOpen(false);
          setQuestionFormData({});
          setIsEditing(false);
          setEditingId(null);
        }}
        className="max-w-2xl"
      >
        <form onSubmit={handleSubmitQuestion}>
          <ModalHeader>
            <h2 className="text-xl font-semibold">
              {isEditing ? 'Edit' : 'Add'} Question
            </h2>
          </ModalHeader>

          <ModalBody className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question Text *</label>
              <textarea
                value={questionFormData.question_text || ''}
                onChange={(e) => setQuestionFormData(prev => ({ ...prev, question_text: e.target.value }))}
                required
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question Type *</label>
              <select
                value={questionFormData.question_type || 'text'}
                onChange={(e) => setQuestionFormData(prev => ({ ...prev, question_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="text">Text</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="textarea">Text Area</option>
                <option value="select">Select Dropdown</option>
                <option value="radio">Radio Buttons</option>
                <option value="checkbox">Checkboxes</option>
                <option value="date">Date</option>
              </select>
            </div>
            {['select', 'radio', 'checkbox'].includes(questionFormData.question_type) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Options (comma-separated)
                </label>
                <Input
                  value={questionFormData.options || ''}
                  onChange={(e) => setQuestionFormData(prev => ({ ...prev, options: e.target.value }))}
                  placeholder="Option 1, Option 2, Option 3"
                />
              </div>
            )}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_required"
                checked={questionFormData.is_required || false}
                onChange={(e) => setQuestionFormData(prev => ({ ...prev, is_required: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_required" className="ml-2 block text-sm text-gray-700">
                Required field
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order *</label>
              <Input
                type="number"
                value={questionFormData.display_order || 1}
                onChange={(e) => setQuestionFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) }))}
                required
              />
            </div>
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsQuestionModalOpen(false);
                setQuestionFormData({});
                setIsEditing(false);
                setEditingId(null);
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'Saving...' : isEditing ? 'Update' : 'Add'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Form Results Modal */}
      <Modal
        isOpen={isResultsModalOpen}
        onClose={() => {
          setIsResultsModalOpen(false);
          setSelectedForm(null);
          setFormResults([]);
          setSelectedSubmission(null);
        }}
        className="max-w-6xl"
      >
        <ModalHeader>
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">
              Form Submissions - {selectedForm?.title}
            </h2>
            <div className="flex space-x-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => downloadFormResults('csv')}
                disabled={!formResults || formResults.length === 0}
                className="h-8 px-3 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
              >
                Download CSV
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => downloadFormResults('xlsx')}
                disabled={!formResults || formResults.length === 0}
                className="h-8 px-3 bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
              >
                Download Excel
              </Button>
            </div>
          </div>
        </ModalHeader>

        <ModalBody>
          <DataTable
            columns={[
              { accessorKey: 'id', header: 'ID', sortable: true },
              { accessorKey: 'user_email', header: 'Email', sortable: true },
              { 
                accessorKey: 'answers', 
                header: 'Responses', 
                cell: (row) => (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedSubmission(row);
                      setIsSubmissionDetailsModalOpen(true);
                    }}
                    className="h-8 px-2 text-blue-600 hover:text-blue-700"
                  >
                    View Details
                  </Button>
                )
              },
              { 
                accessorKey: 'submitted_at', 
                header: 'Submitted At', 
                sortable: true,
                cell: (row) => formatRegistrationSubmittedAt(row.submitted_at)
              }
            ]}
            data={formResults}
            searchKey="user_email"
            searchPlaceholder="Search by email..."
            loading={loading}
            emptyMessage="No submissions yet for this form."
          />
          <p className="mt-2 text-sm text-gray-500">Submitted At: Улаанбаатарын цаг (UTC+8)</p>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setIsResultsModalOpen(false);
              setSelectedForm(null);
              setFormResults([]);
              setSelectedSubmission(null);
            }}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Submission Details Modal */}
      <Modal
        isOpen={isSubmissionDetailsModalOpen}
        onClose={() => {
          setIsSubmissionDetailsModalOpen(false);
          setSelectedSubmission(null);
        }}
        className="max-w-2xl"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold">Submission Answers</h2>
        </ModalHeader>

        <ModalBody className="max-h-[60vh] overflow-y-auto">
          {selectedSubmission && renderSubmissionAnswers(selectedSubmission)}
        </ModalBody>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setIsSubmissionDetailsModalOpen(false);
              setSelectedSubmission(null);
            }}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* FAQs Management Modal */}
      <Modal
        isOpen={isFaqsModalOpen}
        onClose={() => {
          setIsFaqsModalOpen(false);
          setSelectedCourse(null);
          setCourseFaqs([]);
        }}
        className="max-w-5xl"
      >
        <ModalHeader>
          <h2 className="text-xl font-semibold">
            Manage FAQs - {selectedCourse?.name}
          </h2>
        </ModalHeader>

        <ModalBody>
          <div className="mb-4">
            <Button
              onClick={handleCreateFaq}
              className="bg-blue-600 hover:bg-blue-700"
            >
              + Add FAQ
            </Button>
          </div>

          <DataTable
            columns={[
              { accessorKey: 'id', header: 'ID', sortable: true },
              { accessorKey: 'question', header: 'Question', sortable: true },
              { 
                accessorKey: 'answer', 
                header: 'Answer',
                cell: (row) => (
                  <div className="max-w-md">
                    <details className="cursor-pointer">
                      <summary className="text-blue-600 hover:text-blue-700">View Answer</summary>
                      <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                        <p className="whitespace-pre-wrap">{row.answer}</p>
                      </div>
                    </details>
                  </div>
                )
              },
              { accessorKey: 'display_order', header: 'Order', sortable: true },
              {
                accessorKey: 'actions',
                header: 'Actions',
                cell: (row) => (
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditFaq(row)}
                      className="h-8 px-2"
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteFaq(row.id)}
                      className="h-8 px-2 bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                    >
                      Delete
                    </Button>
                  </div>
                )
              }
            ]}
            data={courseFaqs}
            searchKey="question"
            searchPlaceholder="Search FAQs..."
            loading={loading}
            emptyMessage="No FAQs for this course yet."
          />
        </ModalBody>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              setIsFaqsModalOpen(false);
              setSelectedCourse(null);
              setCourseFaqs([]);
            }}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* FAQ Create/Edit Modal */}
      <Modal
        isOpen={isFaqModalOpen}
        onClose={() => {
          setIsFaqModalOpen(false);
          setFaqFormData({});
          setIsEditing(false);
          setEditingId(null);
        }}
        className="max-w-2xl"
      >
        <form onSubmit={handleSubmitFaq}>
          <ModalHeader>
            <h2 className="text-xl font-semibold">
              {isEditing ? 'Edit FAQ' : 'Add FAQ'}
            </h2>
          </ModalHeader>

          <ModalBody>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Question *</label>
                <Input
                  value={faqFormData.question || ''}
                  onChange={(e) => setFaqFormData(prev => ({ ...prev, question: e.target.value }))}
                  required
                  placeholder="What is the duration of the course?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Answer *</label>
                <textarea
                  value={faqFormData.answer || ''}
                  onChange={(e) => setFaqFormData(prev => ({ ...prev, answer: e.target.value }))}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder="The course is 6 months long with flexible scheduling..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Order *</label>
                <Input
                  type="number"
                  value={faqFormData.display_order || 1}
                  onChange={(e) => setFaqFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) }))}
                  required
                />
              </div>
            </div>
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsFaqModalOpen(false);
                setFaqFormData({});
                setIsEditing(false);
                setEditingId(null);
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'Saving...' : isEditing ? 'Update' : 'Add'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
};

export default WebConfig;
